import {
  PageHeaderLeading,
  PageHeaderActionSecondary,
  PageHeaderActions,
  useArtifactCanvas,
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  PageContainer,
  PageHeader,
  Skeleton,
  formatBytes,
} from '@yeshuman/sdk'
import { useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { ExternalLink, FileText, Loader2, Pencil, Trash2 } from 'lucide-react'
import { toast } from 'sonner'
import { useDeleteHealthRecord, useHealthRecord, type HealthRecordItem } from '../hooks/use-health-records'
import {
  formatDateTime,
  formatPercent,
  formatRecordDate,
  SOURCE_LABELS,
  StatusBadge,
  TYPE_LABELS,
} from './health-record-display'
import { HealthRecordEditCanvas } from './health-record-edit-canvas'

function FieldRow({ label, value }: { label: string; value?: string | number | null }) {
  return (
    <div className="min-w-0 space-y-1">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="min-w-0 break-words text-sm [overflow-wrap:anywhere]">
        {value === null || value === undefined || value === '' ? 'Not recorded' : value}
      </p>
    </div>
  )
}

function JsonSection({ title, value }: { title: string; value: Record<string, unknown> }) {
  const hasValue = Object.keys(value || {}).length > 0

  return (
    <Card className="min-w-0 overflow-hidden">
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        <CardDescription>{hasValue ? 'Captured structured data from this record.' : 'No structured data recorded.'}</CardDescription>
      </CardHeader>
      <CardContent className="min-w-0">
        <pre className="max-h-80 min-w-0 max-w-full overflow-auto whitespace-pre-wrap break-words rounded-md bg-muted p-4 text-xs [overflow-wrap:anywhere]">
          {JSON.stringify(value || {}, null, 2)}
        </pre>
      </CardContent>
    </Card>
  )
}

function AttachmentCards({ record }: { record: HealthRecordItem }) {
  const [expanded, setExpanded] = useState<Set<string>>(new Set())

  const toggleExpanded = (id: string) => {
    setExpanded((current) => {
      const next = new Set(current)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  if (record.attachments.length === 0) {
    return (
      <Card className="min-w-0 overflow-hidden">
        <CardHeader>
          <CardTitle>Files</CardTitle>
          <CardDescription>No files attached.</CardDescription>
        </CardHeader>
      </Card>
    )
  }

  return (
    <Card className="min-w-0 overflow-hidden">
      <CardHeader>
        <CardTitle>Files</CardTitle>
        <CardDescription>Stored files and text captured from inspection.</CardDescription>
      </CardHeader>
      <CardContent className="min-w-0 space-y-4">
        {record.attachments.map((attachment) => {
          const file = attachment.file
          const isExpanded = expanded.has(attachment.id)
          const extracted = file.extracted_preview || ''

          return (
            <div key={attachment.id} className="min-w-0 space-y-4 rounded-lg border p-4">
              <div className="flex flex-col gap-4 @md:flex-row @md:items-start @md:justify-between">
                <div className="min-w-0 space-y-1">
                  <p className="truncate text-sm font-medium">{file.original_name}</p>
                  <p className="min-w-0 break-words text-xs text-muted-foreground [overflow-wrap:anywhere]">
                    {file.mime_type} / {formatBytes(file.size_bytes)} / {file.status}
                  </p>
                </div>
                {file.file_url ? (
                  <Button asChild variant="outline" size="sm">
                    <a href={file.file_url} target="_blank" rel="noreferrer">
                      <ExternalLink className="mr-2 h-4 w-4" />
                      Open file
                    </a>
                  </Button>
                ) : null}
              </div>

              {file.parser_note ? (
                <div className="min-w-0 break-words rounded-md bg-muted p-4 text-sm text-muted-foreground [overflow-wrap:anywhere]">
                  {file.parser_note}
                </div>
              ) : null}

              <div className="min-w-0 space-y-2">
                <div className="flex min-w-0 flex-wrap items-center justify-between gap-2">
                  <h3 className="min-w-0 text-sm font-medium">Extracted text</h3>
                  {extracted.length > 1200 ? (
                    <Button variant="ghost" size="sm" onClick={() => toggleExpanded(attachment.id)}>
                      {isExpanded ? 'Show less' : 'Show more'}
                    </Button>
                  ) : null}
                </div>
                {extracted ? (
                  <pre
                    className={
                      isExpanded
                        ? 'min-w-0 max-w-full overflow-auto whitespace-pre-wrap break-words rounded-md bg-muted p-4 text-xs [overflow-wrap:anywhere]'
                        : 'max-h-80 min-w-0 max-w-full overflow-auto whitespace-pre-wrap break-words rounded-md bg-muted p-4 text-xs [overflow-wrap:anywhere]'
                    }
                  >
                    {extracted}
                  </pre>
                ) : (
                  <p className="rounded-md bg-muted p-4 text-sm text-muted-foreground">No extracted text recorded.</p>
                )}
              </div>
            </div>
          )
        })}
      </CardContent>
    </Card>
  )
}

function DetailSkeleton() {
  return (
    <PageContainer className="min-w-0 overflow-x-hidden">
      <div className="@container space-y-6">
        <Skeleton className="h-10 w-40" />
        <Skeleton className="h-28 w-full" />
        <Skeleton className="h-52 w-full" />
      </div>
    </PageContainer>
  )
}

export function HealthRecordDetailPage() {
  const { recordId } = useParams<{ recordId: string }>()
  const navigate = useNavigate()
  const { openCanvas, closeCanvas } = useArtifactCanvas()
  const { record, isLoading, error, refetch } = useHealthRecord(recordId)
  const deleteRecord = useDeleteHealthRecord()

  const subtitle = useMemo(() => {
    if (!record) return 'Health record'
    return `${TYPE_LABELS[record.record_type]} / ${formatRecordDate(record.record_date)} / Updated ${formatDateTime(record.updated_at)}`
  }, [record])

  const handleEdit = () => {
    if (!record) return
    openCanvas({
      preventAutoFocus: true,
      title: 'Edit health record',
      content: <HealthRecordEditCanvas record={record} onClose={closeCanvas} />,
    })
  }

  const handleDelete = async () => {
    if (!record) return
    try {
      await deleteRecord.mutateAsync(record.id)
      toast.success('Health record removed')
      navigate('/records')
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to remove health record')
    }
  }

  if (!recordId) {
    return (
      <PageContainer className="min-w-0 overflow-x-hidden">
        <p className="text-sm text-muted-foreground">Invalid health record.</p>
      </PageContainer>
    )
  }

  if (isLoading) return <DetailSkeleton />

  if (error || !record) {
    return (
      <PageContainer className="min-w-0 overflow-x-hidden">
        <div className="space-y-4">
          <PageHeaderLeading back={{ label: 'Back to records', to: '/records' }} />
          <div className="rounded-md border border-destructive/40 bg-destructive/5 p-4 text-sm text-destructive">
            <div className="flex items-center justify-between gap-4">
              <span>{error instanceof Error ? error.message : 'Could not load health record.'}</span>
              <Button size="sm" variant="outline" onClick={() => void refetch()}>
                Retry
              </Button>
            </div>
          </div>
        </div>
      </PageContainer>
    )
  }

  return (
    <PageContainer className="min-w-0 overflow-x-hidden">
      <div className="min-w-0 space-y-6">
        <PageHeader
          title={record.title}
          description={subtitle}
          icon={<FileText className="h-5 w-5" />}
          leading={<PageHeaderLeading back={{ label: 'Back to records', to: '/records' }} />}
          actions={
            <PageHeaderActions
              secondary={
                <>
                  <PageHeaderActionSecondary onClick={handleEdit}>
                    <Pencil className="h-4 w-4" aria-hidden />
                    Edit
                  </PageHeaderActionSecondary>
                  <Button
                    type="button"
                    variant="destructive"
                    size="sm"
                    className="gap-2"
                    onClick={() => void handleDelete()}
                    disabled={deleteRecord.isPending}
                  >
                    {deleteRecord.isPending ? (
                      <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                    ) : (
                      <Trash2 className="h-4 w-4" aria-hidden />
                    )}
                    Remove
                  </Button>
                </>
              }
            />
          }
        />

        <Card className="min-w-0 overflow-hidden">
          <CardHeader>
            <div className="flex flex-wrap items-center gap-2">
              <Badge variant="secondary">{TYPE_LABELS[record.record_type]}</Badge>
              <StatusBadge status={record.classification_status} />
              <Badge variant="outline">{record.attachment_count} files</Badge>
            </div>
          </CardHeader>
          <CardContent className="grid min-w-0 gap-4 @2col:grid-cols-2 @3col:grid-cols-4">
            <FieldRow label="Confidence" value={formatPercent(record.classification_confidence)} />
            <FieldRow label="Record date" value={formatRecordDate(record.record_date)} />
            <FieldRow label="Visibility" value={record.visibility} />
            <FieldRow label="Source" value={SOURCE_LABELS[record.source] || record.source} />
          </CardContent>
        </Card>

        <div className="grid min-w-0 gap-6 @4col:grid-cols-[minmax(0,1.5fr)_minmax(0,0.8fr)]">
          <div className="min-w-0 space-y-6">
            <Card className="min-w-0 overflow-hidden">
              <CardHeader>
                <CardTitle>Summary</CardTitle>
                <CardDescription>Patient-facing record summary.</CardDescription>
              </CardHeader>
              <CardContent className="min-w-0 space-y-4">
                <div className="min-w-0 break-words rounded-lg bg-muted p-4 text-sm [overflow-wrap:anywhere]">
                  {record.summary || 'No summary recorded.'}
                </div>
                <div className="min-w-0 space-y-2">
                  <h3 className="text-sm font-medium">Description</h3>
                  <p className="min-w-0 break-words rounded-lg bg-muted p-4 text-sm text-muted-foreground [overflow-wrap:anywhere]">
                    {record.description || 'No description recorded.'}
                  </p>
                </div>
              </CardContent>
            </Card>

            <AttachmentCards record={record} />
            <JsonSection title="Structured details" value={record.structured_extraction} />
            <JsonSection title="FHIR hints" value={record.fhir_hints} />
          </div>

          <div className="min-w-0 space-y-6">
            <Card className="min-w-0 overflow-hidden">
              <CardHeader>
                <CardTitle>Filing</CardTitle>
                <CardDescription>Classification and processing details.</CardDescription>
              </CardHeader>
              <CardContent className="min-w-0 space-y-4">
                <FieldRow label="Filing reason" value={record.classification_reason} />
                <FieldRow label="Processing error" value={record.processing_error} />
                <FieldRow label="Processed" value={formatDateTime(record.processed_at)} />
              </CardContent>
            </Card>

            <Card className="min-w-0 overflow-hidden">
              <CardHeader>
                <CardTitle>Provenance</CardTitle>
                <CardDescription>When and how this record was created.</CardDescription>
              </CardHeader>
              <CardContent className="grid min-w-0 gap-4">
                <FieldRow label="Ingestion batch" value={record.ingestion_batch_id} />
                <FieldRow label="Created" value={formatDateTime(record.created_at)} />
                <FieldRow label="Updated" value={formatDateTime(record.updated_at)} />
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </PageContainer>
  )
}
